import { useQuery } from '@apollo/client/react';
import { Box, Button, Heading, Link, Text, VStack } from '@chakra-ui/react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { PageContentRoot } from '#components/page-content-root/page-content-root';
import { PageHeaderPortal } from '#components/page-header-portal/page-header-portal';
import { ProcessorInstancesTable } from '#components/processor-instances-table/processor-instances-table';
import { GET_PROCESSOR } from '#graphql/queries/processor-detail';
import { GET_PROCESSOR_INSTANCES_BY_PROCESSOR } from '#graphql/queries/processor-instances-by-processor';

export function ProcessorDetailPage() {
  const { processorId } = useParams<{ processorId: string }>();

  const { data, loading, error, refetch } = useQuery(GET_PROCESSOR, {
    variables: { id: processorId ?? '' },
    skip: !processorId,
  });

  const { data: instancesData, loading: instancesLoading } = useQuery(
    GET_PROCESSOR_INSTANCES_BY_PROCESSOR,
    {
      variables: { processorId: processorId ?? '', limit: 50 },
      skip: !processorId,
    },
  );

  const detail = data?.processor;
  const instanceRows = instancesData?.processorInstances ?? [];

  const filesLink =
    processorId != null ? (
      <Link asChild color="blue.600" fontSize="sm" fontWeight="medium">
        <RouterLink to={`/processors/${encodeURIComponent(processorId)}/files`}>
          Files
        </RouterLink>
      </Link>
    ) : null;

  if (!processorId) {
    return (
      <PageContentRoot>
        <Text color="red.500">Missing processor id.</Text>
      </PageContentRoot>
    );
  }

  const headerTitleSyncKey = `${processorId}-${loading ? 'l' : ''}${error ? 'e' : ''}${detail ? 'd' : 'n'}`;

  if (loading) {
    return (
      <>
        <PageHeaderPortal title={processorId} titleSyncKey={headerTitleSyncKey}>
          {filesLink}
        </PageHeaderPortal>
        <PageContentRoot>
          <Text color="fg.muted">Loading…</Text>
        </PageContentRoot>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeaderPortal title={processorId} titleSyncKey={headerTitleSyncKey}>
          {filesLink}
        </PageHeaderPortal>
        <PageContentRoot>
          <Text color="red.500">Error: {error.message}</Text>
          <Button mt={4} size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </PageContentRoot>
      </>
    );
  }

  if (!detail) {
    return (
      <>
        <PageHeaderPortal title={processorId} titleSyncKey={headerTitleSyncKey}>
          {filesLink}
        </PageHeaderPortal>
        <PageContentRoot>
          <Heading size="lg" color="fg" mb={4}>
            Processor not found
          </Heading>
          <Text color="fg.muted" mb={4}>
            No processor with id &quot;{processorId}&quot;.
          </Text>
        </PageContentRoot>
      </>
    );
  }

  return (
    <>
      <PageHeaderPortal title={detail.id} titleSyncKey={headerTitleSyncKey}>
        {filesLink}
      </PageHeaderPortal>
      <PageContentRoot>
        <VStack gap={6} align="stretch">
          <VStack align="start" gap={1}>
            <Text fontSize="sm" color="fg.muted">
              Main file id
            </Text>
            <Text fontSize="sm" fontFamily="mono" wordBreak="break-all">
              {detail.fileId}
            </Text>
            <Text fontSize="xs" color="fg.muted" mt={2}>
              Updated {new Date(detail.updatedAt).toLocaleString()}
            </Text>
          </VStack>

          <Box>
            <Heading size="md" color="fg" mb={3}>
              Processor instances
            </Heading>
            <Text fontSize="sm" color="fg.muted" mb={3}>
              Your recent runs of this processor (newest first).
            </Text>
            <ProcessorInstancesTable
              rows={instanceRows}
              loading={instancesLoading}
            />
          </Box>
        </VStack>
      </PageContentRoot>
    </>
  );
}
